import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Truck, Package, Printer, ScanBarcode, XCircle, Calendar, User, Box, Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PAGE_SIZE = 20;

const formatDT = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ReturnToSupplierDocs = () => {
  const navigate = useNavigate();
  const boxUsnRef = useRef(null);

  // List state
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [slips, setSlips] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Detail state
  const [selectedSlipId, setSelectedSlipId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Box creation
  const [boxUsnValue, setBoxUsnValue] = useState('');
  const [boxUsns, setBoxUsns] = useState([]);
  const [creatingBox, setCreatingBox] = useState(false);
  const [printing, setPrinting] = useState({});

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (search.trim()) params.search = search.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await apiClient.get('/damage-checking/return-slips', { params });
      setSlips(r.data.slips || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load return documents');
    } finally {
      setLoading(false);
    }
  }, [search, dateFrom, dateTo, page]);

  useEffect(() => { if (!selectedSlipId) fetchPage(); }, [fetchPage, selectedSlipId]);

  const openDetail = async (slipId) => {
    setSelectedSlipId(slipId);
    setDetail(null);
    setBoxUsns([]);
    setBoxUsnValue('');
    setDetailLoading(true);
    try {
      const r = await apiClient.get(`/damage-checking/return-to-supplier/detail/${encodeURIComponent(slipId)}`);
      setDetail(r.data);
      setTimeout(() => boxUsnRef.current?.focus(), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load document');
      setSelectedSlipId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const reloadDetail = async () => {
    if (!selectedSlipId) return;
    try {
      const r = await apiClient.get(`/damage-checking/return-to-supplier/detail/${encodeURIComponent(selectedSlipId)}`);
      setDetail(r.data);
    } catch { /* ignore */ }
  };

  const backToList = () => { setSelectedSlipId(null); setDetail(null); setBoxUsns([]); setBoxUsnValue(''); };

  const handleBoxUsnScan = (e) => {
    e.preventDefault();
    const code = boxUsnValue.trim();
    if (!code || !detail) return;
    const unboxedCodes = (detail.unboxed_usns || []).map(u => u.usn_code);
    if (!unboxedCodes.includes(code)) {
      toast.error(`USN ${code} is not available to box (not in this slip, or already boxed)`);
      setBoxUsnValue('');
      return;
    }
    if (boxUsns.includes(code)) {
      toast.error(`USN ${code} already added`);
      setBoxUsnValue('');
      return;
    }
    setBoxUsns(prev => [...prev, code]);
    setBoxUsnValue('');
    setTimeout(() => boxUsnRef.current?.focus(), 50);
  };

  const handleCreateBox = async () => {
    if (!selectedSlipId || boxUsns.length === 0) return;
    setCreatingBox(true);
    try {
      const resp = await apiClient.post('/damage-checking/return-to-supplier/create-box', {
        slip_id: selectedSlipId,
        usn_codes: boxUsns,
      });
      toast.success(resp.data.message);
      setBoxUsns([]);
      await reloadDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create box');
    } finally {
      setCreatingBox(false);
    }
  };

  const handlePrintBox = async (boxNumber) => {
    setPrinting(prev => ({ ...prev, [boxNumber]: true }));
    try {
      const r = await apiClient.get(
        `/damage-checking/return-to-supplier/box/${encodeURIComponent(boxNumber)}/packing-slip`,
        { responseType: 'blob' }
      );
      const pdfBlob = r.data.type ? r.data : new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.download = `rts_packing_slip_${boxNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      let detailMsg = err.response?.data?.detail;
      if (!detailMsg && err.response?.data instanceof Blob) {
        try { detailMsg = JSON.parse(await err.response.data.text()).detail; } catch { /* ignore */ }
      }
      toast.error(`Print failed: ${detailMsg || err.message}`);
    } finally {
      setPrinting(prev => ({ ...prev, [boxNumber]: false }));
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ---------- DETAIL VIEW ----------
  if (selectedSlipId) {
    const slip = detail?.slip;
    const unboxed = detail?.unboxed_usns || [];
    const boxes = detail?.boxes || [];
    return (
      <div className="min-h-screen bg-gray-50" data-testid="rts-doc-detail-page">
        <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <Button variant="ghost" size="sm" onClick={backToList} data-testid="detail-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate">{selectedSlipId}</h1>
            <p className="text-xs text-gray-500">Create boxes & print supplier slips</p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto p-4 space-y-4">
          {detailLoading && <p className="text-sm text-gray-500 text-center py-8">Loading…</p>}
          {!detailLoading && slip && (
            <>
              {/* Slip summary */}
              <Card data-testid="rts-doc-summary">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Supplier</p>
                      <p className="font-bold flex items-center gap-1.5"><Truck className="w-4 h-4 text-red-600" />{slip.vendor_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total USNs</p>
                      <p className="font-bold">{detail.total_usns}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Boxed / Unboxed</p>
                      <p className="font-bold">{detail.boxed_count} / {unboxed.length}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="font-medium text-xs">{formatDT(slip.created_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Create Box */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Package className="w-4 h-4" /> Create Box</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {unboxed.length === 0 ? (
                    <p className="text-sm text-gray-500 bg-gray-50 rounded px-3 py-2" data-testid="all-boxed-note">All USNs in this document have been boxed.</p>
                  ) : (
                    <>
                      <form onSubmit={handleBoxUsnScan} className="flex gap-2">
                        <Input
                          ref={boxUsnRef}
                          value={boxUsnValue}
                          onChange={e => setBoxUsnValue(e.target.value)}
                          placeholder={`Scan USN (${unboxed.length} available)`}
                          data-testid="box-usn-input"
                        />
                        <Button type="submit" disabled={!boxUsnValue.trim()} data-testid="box-usn-submit"><ScanBarcode className="w-4 h-4" /></Button>
                      </form>
                      {boxUsns.length > 0 && (
                        <div className="space-y-1.5 max-h-48 overflow-y-auto">
                          {boxUsns.map((u, idx) => (
                            <div key={u} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm" data-testid={`box-usn-row-${u}`}>
                              <span className="font-mono">{u}</span>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400"
                                onClick={() => setBoxUsns(prev => prev.filter((_, i) => i !== idx))}
                                data-testid={`remove-box-usn-${u}`}>
                                <XCircle className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <Button className="w-full bg-red-600 hover:bg-red-700" onClick={handleCreateBox}
                        disabled={creatingBox || boxUsns.length === 0} data-testid="create-box-btn">
                        {creatingBox ? 'Creating…' : `Create Box — ${boxUsns.length} USN${boxUsns.length !== 1 ? 's' : ''}`}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Existing boxes */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Boxes className="w-4 h-4" /> Boxes ({boxes.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {boxes.length === 0 ? (
                    <p className="text-sm text-gray-500">No boxes created yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {boxes.map((b) => (
                        <div key={b.box_number} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2" data-testid={`box-row-${b.box_number}`}>
                          <div className="min-w-0">
                            <p className="font-mono font-medium text-sm truncate">{b.box_number}</p>
                            <p className="text-xs text-gray-500">{b.total_qty} USN{b.total_qty !== 1 ? 's' : ''}</p>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => handlePrintBox(b.box_number)}
                            disabled={!!printing[b.box_number]} data-testid={`print-box-${b.box_number}`}>
                            <Printer className="w-3.5 h-3.5 mr-1.5" />
                            {printing[b.box_number] ? 'Preparing…' : 'Print'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    );
  }

  // ---------- LIST VIEW ----------
  return (
    <div className="min-h-screen bg-gray-50" data-testid="rts-docs-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-lg font-bold text-gray-900">Return to Supplier — Documents</h1>
          <p className="text-xs text-gray-500">Create boxes & print supplier slips for saved returns</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  placeholder="Search slip # / supplier"
                  className="pl-8"
                  data-testid="search-input"
                />
              </div>
              <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }} data-testid="date-from-input" />
              <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }} data-testid="date-to-input" />
            </div>
          </CardContent>
        </Card>

        {/* List */}
        <div className="space-y-2">
          {loading && <p className="text-sm text-gray-500 text-center py-8">Loading…</p>}
          {!loading && slips.length === 0 && (
            <Card><CardContent className="py-12 text-center text-gray-500 text-sm">
              <Box className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No return documents found
            </CardContent></Card>
          )}
          {!loading && slips.map((s) => {
            const fullyBoxed = (s.usns_boxed || 0) >= (s.total_usns || 0) && (s.total_usns || 0) > 0;
            return (
              <Card key={s.slip_id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => openDetail(s.slip_id)} data-testid={`slip-row-${s.slip_id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                      <Truck className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2">
                        <p className="font-mono font-bold text-sm">{s.slip_id}</p>
                        {fullyBoxed ? (
                          <Badge className="text-[10px] bg-green-100 text-green-700">Boxed</Badge>
                        ) : (s.boxes_created > 0 ? (
                          <Badge className="text-[10px] bg-amber-100 text-amber-700">Partly boxed</Badge>
                        ) : (
                          <Badge className="text-[10px] bg-gray-100 text-gray-600">No box</Badge>
                        ))}
                      </div>
                      <p className="text-sm font-medium mt-0.5 truncate">{s.vendor_name || '-'}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs text-gray-600">
                        <span className="flex items-center gap-1"><Package className="w-3 h-3" />{s.total_usns || 0} USNs · {s.boxes_created || 0} box{(s.boxes_created || 0) !== 1 ? 'es' : ''} ({s.usns_boxed || 0} boxed)</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDT(s.created_at)}</span>
                        {s.staff_name && <span className="flex items-center gap-1"><User className="w-3 h-3" />{s.staff_name}</span>}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openDetail(s.slip_id); }} data-testid={`open-slip-${s.slip_id}`}>
                      Open
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-gray-500">Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="prev-page">Prev</Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReturnToSupplierDocs;
